import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { UserInterface } from '../_interfaces/user.interface';

interface CreateUserInterface {
  username: string;
  email: string;
  role: string;
}

export const RoleKeyLabelMap: Record<string, string> = {
  'basic-user': 'Read',
  'write-user': 'Write',
  'power-user': 'Write+',
  admin: 'Admin'
};

export const RoleLabelKeyMap: Record<string, string> = {
  Read: 'basic-user',
  Write: 'write-user',
  'Write+': 'power-user',
  Admin: 'admin'
};

export type AccessLevel = 'read' | 'update' | 'write' | 'all';

export interface GetUsersQueryInterface {
  role?: string | null;
  filter?: string | null;
  access_level?: AccessLevel;
  feature?: string;
  internal?: boolean;
  page_size?: string;
  start_token?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AdminService {
  private http = inject(HttpClient);

  private usersEndpoint = environment.apiUrl + '/v2/access-control/users';

  private currentUserRoles: string[] = [];

  public getUsers(query: GetUsersQueryInterface): Observable<UserInterface[]> {
    const params = new URLSearchParams();

    if (query.role) {
      params.set('role', query.role);
    }
    if (query.filter) {
      params.set('filter', query.filter.toLowerCase());
    }
    if (query.access_level) {
      params.set('access_level', query.access_level);
    }
    if (query.feature) {
      params.set('feature', query.feature);
    }
    if (environment.customerIdentifier === 'sw-tst') {
      params.set('internal', String(query.internal ?? true));
    } else if (query.internal !== undefined) {
      params.set('internal', String(query.internal));
    }
    if (query.page_size) {
      params.set('page_size', query.page_size);
    }
    if (query.start_token) {
      params.set('start_token', query.start_token);
    }

    const queryString = params.toString();

    return this.http.get<{ users: UserInterface[] }>(
      queryString ? `${this.usersEndpoint}?${queryString}` : this.usersEndpoint
    ).pipe(
      map((response: { users: UserInterface[] }) => {
        response.users = this.mapBackendPermissionLabels(response.users);
        return response.users;
      })
    );
  }

  private mapBackendPermissionLabels(users: UserInterface[]): UserInterface[] {
    return users.map(user => {
      user.roles = user.roles.map(role => {
        return RoleKeyLabelMap[role];
      });
      if (
        user.roles.includes('Read') &&
        user.roles.includes('Write') &&
        user.roles.includes('Write+')
      ) {
        user.roles = ['Full'];
      }
      return user;
    });
  }

  public getRolesByUserId(userId: string, isCurrentUser = false): Observable<{ roles: string[] }> {
    if (isCurrentUser && this.currentUserRoles?.length) {
      return of({ roles: this.currentUserRoles });
    }
    return this.http
      .get<{ roles: string[] }>(this.usersEndpoint + `/${userId}/roles`)
      .pipe(
        map(roles => {
          if (isCurrentUser) {
            this.currentUserRoles = roles.roles;
          }
          return roles;
        })
      );
  }

  public createUser(user: CreateUserInterface): Observable<UserInterface> {
    return this.http.post<UserInterface>(
      this.usersEndpoint,
      user
    );
  }

  public deleteUser(userId: string): Observable<UserInterface> {
    return this.http.delete<UserInterface>(
      this.usersEndpoint + `/${userId}`,
      {}
    );
  }

  public updateUser(userId: string, role: string): Observable<void> {
    return this.http.patch<void>(
      this.usersEndpoint + `/${userId}/roles`,
      { roles: [role] }
    );
  }

  public isAdmin(): Observable<boolean> {
    return this.getRolesByUserId(localStorage.getItem('cognitoUserId') || '').pipe(
      map((res: { roles: string[] }) => res.roles.includes('admin'))
    );
  }
}
